import React from "react"
import PropTypes from 'prop-types';
import {graphql} from "gatsby";
import { Layout, SliceZone, SEO } from "../components";
import "../styles/style.scss";

const Page = (props) => {
  const { location, path, data: { prismicPage: { data } }} = props;
  return (
    <Layout path={path} landing_page={data.landing_page}>
      <SEO title={data.seo_title} description={data.seo_description} pathname={location.pathname} />
      <SliceZone slices={data.body} pathname={location.pathname} />
    </Layout>
  );
};

export default Page;

Page.propTypes = {
  path: PropTypes.string,
  data: PropTypes.shape({
    prismicPage: PropTypes.shape({
      data: PropTypes.shape({
        title: PropTypes.shape({
          text: PropTypes.string
        }).isRequired,
        seo_title: PropTypes.string,
        seo_description: PropTypes.string,
        body: PropTypes.array
      })
    })
  })
};

export const pageQuery = graphql`
 query PageBySlug($uid: String!) {
    prismicPage(uid: { eq: $uid }) {
      uid
      data {
        title {
          text
        }
        seo_description
        seo_title
        landing_page
        body {
          ... on PrismicPageBodyAccordion {
            id
            slice_type
            items {
              content {
                html
                raw
              }
              heading {
                text
              }
              image {
                alt
                url
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1320, quality: 90) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyHero {
            id
            slice_type
            primary {
              cta
              hero_title
              content {
                html
                raw
              }
              image {
                alt
                url
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 800, quality: 94) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyCta {
            id
            slice_type
            primary {
              background_color
              custom_content {
                raw
                html
              }
              custom_cta_button_text
              custom_cta_link {
                url
                target
                type
                link_type
              }
              type
              custom_anchor
              custom_cta_centered
            }
          }
          ... on PrismicPageBodyColumnLayout {
            id
            items {
              column_content {
                html
                raw
              }
              icon {
                raw
              }
            }
            slice_type
            primary {
              content {
                html
                raw
                text
              }
              heading {
                text
                raw
              }
              background
              column_background_type
              column_text_orientation
            }
          }
          ... on PrismicPageBodyCenteredOneColumn {
            id
            primary {
              class_name
              content {
                html
                raw
              }
              heading {
                text
              }
              embedded {
                html
                embed_url
              }
              image {
                url
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 1100, quality: 90) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
            }
            slice_type
          }
          ... on PrismicPageBodyInfo {
            id
            items {
              column_content {
                raw
              }
            }
            primary {
              section_background
              box_background
            }
            slice_type
          }
          ... on PrismicPageBodySectionSeparator {
            id
            primary {
              id
              section_background
              padding
              has_border_line
            }
            slice_type
          }
          ... on PrismicPageBodyCalculator {
            id
            slice_type
          }
          ... on PrismicPageBodyPricing {
            id
            slice_type
            primary {
              type
            }
          }
          ... on PrismicPageBodyHubspotForm {
            id
            slice_type
            primary {
              form_id
              portal_id
            }
          }
          ... on PrismicPageBodyTrialForm {
            id
            slice_type
            primary {
              content {
                raw
              }
              form_title {
                raw
              }
              language
              subject
              autofocus_email
              thank_you_page {
                document {
                  ... on PrismicPage {
                    url
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyForm {
            id
            slice_type
            primary {
              form_type
              form_sent_content {
                raw
              }
            }
          }
          ... on PrismicPageBodyArticles {
            id
            primary {
              link_to_category {
                document {
                  ... on PrismicCategory {
                    id
                    uid
                    url
                  }
                }
              }
            }
            items {
              article {
                id
                document {
                  ... on PrismicArticle {
                    data {
                      title {
                        text
                      }
                      content {
                        raw
                      }
                      image {
                        thumbnails {
                          list {
                            alt
                            localFile {
                              childImageSharp {
                                fluid(maxWidth: 640, quality: 90) {
                                  ...GatsbyImageSharpFluid
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                    url
                  }
                }
              }
            }
            primary {
              description {
                raw
              }
              heading {
                raw
              }
            }
            slice_type
          }
          ... on PrismicPageBodyCarousel {
            id
            items {
              item_content {
                raw
              }
              item_embed {
                html
                embed_url
              }
              item_link {
                url
              }
              item_title {
                raw
              }
              item_image {
                  url
                  localFile {
                    childImageSharp {
                      fluid(maxWidth: 800, quality: 90) {
                        ...GatsbyImageSharpFluid
                      }
                    }
                  }
                }
            }
            slice_type
          }
          ... on PrismicPageBodyTwoColumnsSection {
            id
            items {
              column_content {
                html
                raw
              }
              column_embed {
                html
                embed_url
              }
              column_title {
                text
              }
              icon {
                raw
              }
              image {
                url
                localFile {
                  childImageSharp {
                    fluid(maxWidth: 800, quality: 90) {
                      ...GatsbyImageSharpFluid
                    }
                  }
                }
              }
              image_side
              image_with_shadows
            }
            primary {
              section_background
              content {
                html
                raw
                text
              }
              heading {
                text
              }
            }
            slice_type
          }
          ... on PrismicPageBodyFeatureRow {
            id
            slice_type
            items {
              feature
              description
            }
            primary {
              feature_title {
                text
                raw
              }
            }
          }
          ... on PrismicPageBodyCustomerQuote {
            id
            slice_type
            items {
              quote {
                id
                document {
                  ... on PrismicCustomerQuote {
                    data {
                      name
                      quote {
                        raw
                      }
                    }
                    id
                  }
                }
              }
            }
          }
          ... on PrismicPageBodyCustomForm {
            id
            primary {
              form {
                document {
                  ... on PrismicPageForm {
                    id
                    data {
                      form_name {
                        text
                      }
                      subject
                      submit_button_text
                      body {
                        ... on PrismicPageFormBodyFormInput {
                          id
                          slice_type
                          primary {
                            name
                            max_length
                            required
                            label
                            placeholder
                            input_type
                          }
                        }
                        ... on PrismicPageFormBodyTextarea {
                          id
                          primary {
                            label
                            max_length
                            name
                            placeholder
                            required
                          }
                          slice_type
                        }
                        ... on PrismicPageFormBodySelect {
                          id
                          primary {
                            label
                            name
                            placeholder
                            required
                          }
                          items {
                            item
                          }
                          slice_type
                        }
                      }
                    }
                  }
                }
              }
              content {
                raw
              }
              thank_you_content {
                raw
              }
              heading {
                raw
              }
            }
            slice_type
          }
        }
      }
    }
  }
`;
